import {
  MAIL_ARCHIVE_TYPE,
  type ArchiveSearchResult,
  type ArchiveType,
  type DataModelState,
  type FieldListDto,
  type SoftwareDto,
} from '@bk/contracts';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import {
  archiveFilters,
  archiveKind,
  choiceField,
  fieldLabel,
  type ColumnFilter,
  type Sort,
} from '../../lib/field-values';
import { ColumnControl, SortHeading, sortAria } from './ListColumn';
import { useFieldLists } from './FieldInput';
import { dateTime, humanize } from '../../lib/format';
import { navigate } from '../../lib/router';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Badge, Button, Card, Empty, ErrorText, PageTitle } from '../../ui';

const PAGE = 50;

type Row = ArchiveSearchResult['righe'][number];

/** Da dove arriva un tipo: il master del registro dei tipi, con il nome del programma se è un collegamento. */
function origin(type: ArchiveType, software: SoftwareDto[]): string {
  if (type.master === 'modello') return 'Ricavati dal sistema';
  if (type.master === 'archive') return 'File caricati';
  if (type.nome === MAIL_ARCHIVE_TYPE) return 'Posta';
  const s = software.find((x) => x.key === type.master);
  return `Programma: ${s?.product ?? s?.name ?? humanize(type.master)}`;
}

function RowDetail({
  row,
  type,
  lists,
  onClose,
}: {
  row: Row;
  type: ArchiveType;
  lists: Map<string, FieldListDto>;
  onClose: () => void;
}) {
  const labels = new Map(type.attributi.map((a) => [a.nome, a.etichetta ?? humanize(a.nome)]));
  // Prima gli attributi del tipo nel loro ordine, poi quelli arrivati in più dalla fonte.
  const keys = [
    ...type.attributi.map((a) => a.nome),
    ...Object.keys(row.attributi).filter((k) => !labels.has(k)),
  ];
  return (
    <Card>
      <div className="mb-3 flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold">Dettaglio</h3>
          <p className="text-xs text-ink/50">
            {row.riferimento ? `Riferimento ${row.riferimento} · ` : ''}
            {row.fonte} · {row.modalita} · acquisito {fieldLabel({ forma: 'data_ora' }, row.acquisito_il)} · versione{' '}
            {row.versione}
          </p>
          {row.rimosso_il && (
            <p className="mt-1 text-xs text-red-700">
              Rimosso dalla fonte il {dateTime(row.rimosso_il)}
            </p>
          )}
        </div>
        <Button variant="ghost" onClick={onClose}>
          Chiudi
        </Button>
      </div>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-[12rem_minmax(0,1fr)]">
        {keys.map((k) => (
          <div key={k} className="contents">
            <dt className="text-ink/55">{labels.get(k) ?? humanize(k)}</dt>
            <dd className="whitespace-pre-wrap break-words">
              {fieldLabel(
                type.attributi.find((a) => a.nome === k),
                row.attributi[k],
                lists,
              ) || '—'}
            </dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

function TypeRows({ type }: { type: ArchiveType }) {
  const { tenant, t } = useTenant();
  const lists = useFieldLists(type.attributi);
  const [filters, setFilters] = useState<Record<string, ColumnFilter>>({});
  const [sort, setSort] = useState<Sort>({
    key: type.attributi[0]?.nome ?? '__acquisito_il',
    direction: 'asc',
  });
  const updateSort = (s: Sort) => {
    setSort(s);
    setOffset(0);
  };
  const fields = [
    ...type.attributi,
    { nome: '__acquisito_il', tipo: 'data' as const, forma: 'data_ora' },
  ];
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  const [removed, setRemoved] = useState(false);
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState<string | null>(null);
  const result = useQuery({
    queryKey: [
      'tenant',
      tenant.id,
      'archive',
      'search',
      type.nome,
      query,
      removed,
      offset,
      filters,
      sort,
      fields,
    ],
    queryFn: () =>
      api<ArchiveSearchResult>(t('/archive/search'), {
        body: {
          tipo: type.nome,
          testo: query || null,
          rimossi: removed,
          limite: PAGE,
          offset,
          elenchi: fields.filter((a) => choiceField(a) && a.forma !== 'si_no').map((a) => a.nome),
          filtri: archiveFilters(fields, filters),
          ordina_per: sort.key,
          direzione: sort.direction,
          tipo_ordine: archiveKind(fields.find((a) => a.nome === sort.key)),
        },
      }),
    placeholderData: (prev) => prev,
  });
  const data = result.data;
  const columns = type.attributi;
  const selected = data?.righe.find((r) => r.id === open);
  return (
    <div className="space-y-4">
      <Card>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold">{type.etichetta}</h2>
            <p className="text-xs text-ink/50">
              {type.entita} righe{type.rimossi ? ` · ${type.rimossi} rimosse` : ''} ·{' '}
              {type.attributi.length} attributi
              {type.aggiornato_il ? ` · ultimo aggiornamento ${dateTime(type.aggiornato_il)}` : ''}
            </p>
          </div>
          <label className="flex items-center gap-1.5 text-xs text-ink/60">
            <input
              type="checkbox"
              checked={removed}
              onChange={(e) => {
                setRemoved(e.target.checked);
                setOffset(0);
              }}
            />
            Anche le rimosse
          </label>
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setQuery(text.trim());
              setOffset(0);
            }}
          >
            <input
              className="w-48 rounded-lg border border-black/15 px-3 py-1.5 text-sm"
              placeholder="Cerca in tutti i campi…"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <Button variant="ghost" type="submit">
              Cerca
            </Button>
          </form>
        </div>
        <ErrorText>{result.error ? errorMessage(result.error) : ''}</ErrorText>
        <div className="overflow-x-auto">
          <table className="bk-list">
            <thead>
              <tr>
                {columns.map((a) => (
                  <th key={a.nome} aria-sort={sortAria(sort, a.nome)}>
                    <SortHeading
                      label={a.etichetta ?? humanize(a.nome)}
                      field={a.nome}
                      sort={sort}
                      onSort={updateSort}
                    />
                  </th>
                ))}
                <th aria-sort={sortAria(sort, '__acquisito_il')}>
                  <SortHeading
                    label="Acquisito"
                    field="__acquisito_il"
                    sort={sort}
                    onSort={updateSort}
                  />
                </th>
              </tr>
              <tr>
                {fields.map((a) => (
                  <th key={a.nome}>
                    <ColumnControl
                      attr={a}
                      label={
                        a.etichetta ??
                        (a.nome === '__acquisito_il' ? 'Acquisito' : humanize(a.nome))
                      }
                      value={filters[a.nome] ?? {}}
                      options={
                        choiceField(a) && a.forma !== 'si_no'
                          ? (data?.opzioni?.[a.nome] ?? []).map((v) => ({
                              value: v,
                              label: fieldLabel(a, v, lists),
                            }))
                          : undefined
                      }
                      onChange={(v) => {
                        setFilters({ ...filters, [a.nome]: v });
                        setOffset(0);
                      }}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data?.righe.map((r) => (
                <tr
                  key={r.id}
                  className={`cursor-pointer ${open === r.id ? 'font-medium text-accent' : ''} ${r.rimosso_il ? 'text-ink/40 line-through' : ''}`}
                  onClick={() => setOpen(open === r.id ? null : r.id)}
                >
                  {columns.map((a) => (
                    <td
                      key={a.nome}
                      className="max-w-[16rem] truncate"
                      title={fieldLabel(a, r.attributi[a.nome], lists)}
                    >
                      {fieldLabel(a, r.attributi[a.nome], lists) || '—'}
                    </td>
                  ))}
                  <td className="whitespace-nowrap text-xs text-ink/45">
                    {fieldLabel({ forma: 'data_ora' }, r.acquisito_il)}
                    {r.versione > 1 && ` · v${r.versione}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data && !data.righe.length && (
            <p className="py-4 text-center text-sm text-ink/50">
              Nessuna riga{query ? ' per questa ricerca' : ''}.
            </p>
          )}
        </div>
        {data && data.totale > 0 && (
          <div className="mt-3 flex items-center gap-2 text-xs text-ink/55">
            <span className="flex-1">
              {offset + 1}–{offset + data.righe.length} di {data.totale}
            </span>
            <Button
              variant="ghost"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE))}
            >
              Precedenti
            </Button>
            <Button
              variant="ghost"
              disabled={offset + PAGE >= data.totale}
              onClick={() => setOffset(offset + PAGE)}
            >
              Successive
            </Button>
          </div>
        )}
      </Card>
      {selected && (
        <RowDetail
          key={selected.id}
          row={selected}
          type={type}
          lists={lists}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
}

/** Dati › Tutti i dati salvati: ciò che il sistema ha salvato nell'archivio dell'azienda, per tipo e provenienza. Sola lettura. */
export function StoredDataPage({ typeKey }: { typeKey?: string }) {
  const { tenant, isManager } = useTenant();
  const types = useTenantQuery<{ data: ArchiveType[] }>(['archive', 'types'], '/archive/types');
  const model = useTenantQuery<DataModelState>(['data-model'], '/data-model', {
    enabled: isManager,
  });
  const software = useTenantQuery<{ data: SoftwareDto[] }>(['software'], '/software');
  // La posta dell'azienda la leggono solo i responsabili.
  // Nel menu solo i tipi con almeno un dato, in ordine alfabetico.
  const list = (types.data?.data ?? [])
    .filter((x) => (isManager || x.nome !== MAIL_ARCHIVE_TYPE) && x.entita > 0)
    .sort((a, b) => a.etichetta.localeCompare(b.etichetta, 'it', { sensitivity: 'base' }));
  const chosen = list.find((x) => x.nome === typeKey) ?? list[0];
  const modelType = model.data?.data.find((x) => x.chiave === chosen?.nome);
  const current = chosen && {
    ...chosen,
    attributi: chosen.attributi.map((a) => ({
      ...a,
      ...modelType?.attributi.find((f) => f.nome === a.nome),
    })),
  };
  const groups = new Map<string, ArchiveType[]>();
  for (const x of list) {
    const g = origin(x, software.data?.data ?? []);
    groups.set(g, [...(groups.get(g) ?? []), x]);
  }
  return (
    <>
      <PageTitle
        title="Tutti i dati salvati"
        subtitle="Ciò che il sistema ha salvato nell’archivio dell’azienda: dai programmi collegati, dai file caricati, dalla posta e dal modello dell’azienda. Sola lettura: si corregge nella fonte."
      />
      <ErrorText>{types.error ? errorMessage(types.error) : ''}</ErrorText>
      {types.data && !list.length ? (
        <Empty>
          Nessun dato salvato ancora: arrivano collegando un programma o caricando un export (CSV o
          Excel) in Archivio.
        </Empty>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[16rem_minmax(0,1fr)]">
          <nav className="space-y-4">
            {[...groups]
              .sort(([a], [b]) => a.localeCompare(b, 'it'))
              .map(([group, items]) => (
                <div key={group}>
                  <p className="mb-1 px-2 text-xs font-medium uppercase tracking-wide text-ink/45">
                    {group}
                  </p>
                  <ul>
                    {items.map((x) => (
                      <li key={x.nome}>
                        <button
                          className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-black/5 ${current?.nome === x.nome ? 'bg-black/5 font-medium' : ''}`}
                          onClick={() => navigate(`/t/${tenant.id}/dati/salvati/${x.nome}`)}
                        >
                          <span className="min-w-0 flex-1 truncate">{x.etichetta}</span>
                          <Badge>{x.entita}</Badge>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
          </nav>
          <div className="min-w-0">{current && <TypeRows key={current.nome} type={current} />}</div>
        </div>
      )}
    </>
  );
}
